import React, { useCallback, useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { trackEvent } from '@fs/zion-analytics'
import {
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  H3,
  Paragraph,
  Separator,
  useOverlay,
} from '@fs/zion-ui'
import { setPreference, usePreference } from '@fs/zion-preferences'
import { Link } from '@fs/zion-router'
import { css } from '@emotion/core'
import useSourceCentricDetailsFlag from '../../data-service/useSourceCentricDetailsFlag'
import preferenceKeys from '../preference-keys'

// TODO(TREEWEB-12092): replace with the real help article URL once it is published
const HELP_ARTICLE_URL = 'https://www.familysearch.org/en/help/helpcenter/'

const listCss = css`
  li {
    margin: 4px 20px;
  }
`

// The overlay never opens before this date, and bumping the date past users' stored
// dismissal times shows the overlay again (e.g. whenever the What's New content changes)
export const WHATS_NEW_UPDATED_AT = Date.UTC(2026, 6, 23)

export default function SourceCentricSummaryOverlay(): JSX.Element | null {
  const [t] = useTranslation()
  const overlay = useOverlay()
  const isSourceCentricOn = !!useSourceCentricDetailsFlag().data?.isOn
  // number when previously dismissed, '' when never dismissed, undefined while loading, null when signed out
  const dismissedAt = usePreference<number>(preferenceKeys.SCPP_SUMMARY_DISMISSED_AT)
  const hasOpened = useRef(false)

  const handleDismissed = useCallback(async () => {
    try {
      // setPreference is async and rejects on save failures, so await it to actually catch them
      await setPreference(preferenceKeys.SCPP_SUMMARY_DISMISSED_AT, Date.now())
    } catch (error) {
      console.error('Error saving dialog dismissal preference:', error)
    } finally {
      overlay.close()
    }
  }, [overlay])

  const { open } = overlay

  // Impression event gives the denominator for the What's New expand and Dismiss click-through rates
  const openWithTracking = useCallback(() => {
    trackEvent({ event_name: 'SCPP Summary Overlay Displayed' })
    open()
  }, [open])

  useEffect(() => {
    // usePreference does not refresh after setPreference, so the once-per-mount guard
    // keeps a re-run of this effect from reopening the overlay after it was dismissed
    if (hasOpened.current || !isSourceCentricOn || Date.now() < WHATS_NEW_UPDATED_AT) {
      return
    }

    const neverDismissed = dismissedAt === ''
    const dismissedBeforeUpdate = typeof dismissedAt === 'number' && dismissedAt < WHATS_NEW_UPDATED_AT

    if (neverDismissed || dismissedBeforeUpdate) {
      hasOpened.current = true
      openWithTracking()
    }
  }, [dismissedAt, isSourceCentricOn, openWithTracking])

  if (overlay.isOpen || overlay.transitioning)
    return (
      <DialogOverlay
        title={t('tree-person.scpp-summary-overlay.title')}
        {...overlay}
        closeOnClickAway={false}
        nonClosable
      >
        <Paragraph size="sm">
          <Trans
            i18nKey="tree-person.scpp-summary-overlay.description"
            components={{
              L: <Link external target="_blank" to={HELP_ARTICLE_URL} linkName="SCPP Summary: Learn More" />,
            }}
          />
        </Paragraph>

        <Paragraph size="sm">{t('tree-person.scpp-summary-overlay.feedback')}</Paragraph>

        <Separator size="nano" />

        <Expander linkName="SCPP Summary: What's New">
          <ExpanderHeader
            closedTitle={t('tree-person.scpp-summary-overlay.expand-whats-new.action')}
            openedTitle={t('tree-person.scpp-summary-overlay.collapse-whats-new.action')}
            data-testid="scpp-summary-whats-new"
          >
            <H3 size="H6">{t('tree-person.scpp-summary-overlay.whats-new.heading')}</H3>
          </ExpanderHeader>

          <ExpanderContent>
            <ul css={listCss}>
              <li>{t('tree-person.scpp-summary-overlay.list-item-1.description')}</li>
              <li>
                <Trans i18nKey="tree-person.scpp-summary-overlay.list-item-2.description" />
              </li>
              <li>
                <Trans i18nKey="tree-person.scpp-summary-overlay.list-item-3.description" />
              </li>
              <li>
                <Trans i18nKey="tree-person.scpp-summary-overlay.list-item-4.description" />
              </li>
            </ul>
          </ExpanderContent>
        </Expander>

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button
              emphasis="medium"
              linkName="SCPP Summary: Dismiss"
              onClick={handleDismissed}
              data-testid="scpp-summary-dismiss"
            >
              {t('common-ui:dismiss.action')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    )

  return null
}
